import { useCallback, useMemo } from 'react';
import { useLocation, useMatches } from 'react-router';

export enum routes {
  home = '/',
  login = '/login',
  dashboard = '/dashboard',
  analytics = '/analytics',
  analyticsCampaignDetail = '/analytics/:campaignId',
  audiences = '/audiences',
  payments = '/payments',
  ledger = '/ledger',
  /** Campaign management: overview + the table of every campaign. */
  campaign = '/campaign',
  /** The campaign builder. Was `/campaign` before the management page existed. */
  campaignCreate = '/campaign/create',
  aiCopilot = '/ai-copilot',
  emailVerification = '/email-verification',
  accountSettings = '/settings',
  accountSetup = '/account-setup',
  creativeSuite = `/creative-suite`,
  creativeSuiteEditor = `/creative-suite/editor`,

  // Admin routes
  adminLogin = '/admin/login',
  adminDashboard = '/admin/dashboard',
  adminTenants = '/admin/tenants',
  adminCampaigns = '/admin/campaigns',
  adminCampaignDetail = '/admin/campaigns/:id',
  adminTenantCreate = '/admin/tenants/new',
  adminTenantEdit = '/admin/tenants/:id/edit',
  adminTenantDetail = '/admin/tenants/:id',
  adminBusinesses = '/admin/businesses',
  adminReferenceData = '/admin/reference-data',
  adminBusinessDetail = '/admin/businesses/:id',
  adminAuditLogs = '/admin/audit-logs',
  adminPaymentsHealth = '/admin/payments-health',

  // Tenant staff routes
  tenantStaffLogin = '/tenant-staff/login',
  tenantStaffDashboard = '/tenant-staff/dashboard',
  tenantStaffCampaigns = '/tenant-staff/campaigns',
  tenantStaffAdvertisers = '/tenant-staff/advertisers',
  tenantStaffBusinessDetail = '/tenant-staff/advertisers/:businessId',
  tenantStaffUsers = '/tenant-staff/users',
  tenantStaffStaff = '/tenant-staff/staff',
  tenantStaffConsolidatedReport = '/tenant-staff/reports',
}

type routeMeta = {
  path: routes;
  title: string;
};

export const routesMeta: routeMeta[] = [
  {
    path: routes.home,
    title: 'Home',
  },
  {
    path: routes.login,
    title: 'Login',
  },
  {
    path: routes.dashboard,
    title: 'Dashboard',
  },
  {
    path: routes.analytics,
    title: 'Analytics',
  },
  {
    path: routes.analyticsCampaignDetail,
    title: 'Campaign Analytics',
  },
  {
    path: routes.payments,
    title: 'Payments',
  },
  {
    path: routes.ledger,
    title: 'Account Ledger',
  },
  {
    path: routes.campaign,
    title: 'Campaigns',
  },
  {
    path: routes.campaignCreate,
    title: 'Create Campaign',
  },
  {
    path: routes.creativeSuite,
    title: 'Creative Suite',
  },
  {
    path: routes.creativeSuiteEditor,
    title: 'Creative Template Editor',
  },
  {
    path: routes.accountSetup,
    title: 'Account Setup',
  },
  {
    path: routes.emailVerification,
    title: 'Email Verification',
  },
  {
    path: routes.accountSettings,
    title: 'Account Settings',
  },
  {
    path: routes.adminLogin,
    title: 'Admin Login',
  },
  {
    path: routes.adminDashboard,
    title: 'Admin Dashboard',
  },
  {
    path: routes.adminTenants,
    title: 'Admin Tenants',
  },
  {
    path: routes.adminCampaigns,
    title: 'Admin Campaigns',
  },
  {
    path: routes.adminCampaignDetail,
    title: 'Campaign Details',
  },
  {
    path: routes.adminTenantCreate,
    title: 'Create Tenant',
  },
  {
    path: routes.adminTenantEdit,
    title: 'Edit Tenant',
  },
  {
    path: routes.adminTenantDetail,
    title: 'Tenant Details',
  },
  {
    path: routes.adminBusinesses,
    title: 'Admin Businesses',
  },
  {
    path: routes.adminBusinessDetail,
    title: 'Business Details',
  },
  {
    path: routes.adminAuditLogs,
    title: 'Audit Logs',
  },
  {
    path: routes.adminPaymentsHealth,
    title: 'Payments Health',
  },
  {
    path: routes.tenantStaffLogin,
    title: 'Tenant Staff Login',
  },
  {
    path: routes.tenantStaffDashboard,
    title: 'Tenant Staff Dashboard',
  },
  {
    path: routes.tenantStaffCampaigns,
    title: 'Tenant Staff Campaigns',
  },
  {
    path: routes.tenantStaffAdvertisers,
    title: 'Advertisers',
  },
  {
    path: routes.tenantStaffBusinessDetail,
    title: 'Advertiser Detail',
  },
  {
    path: routes.tenantStaffUsers,
    title: 'Tenant Staff Users',
  },
  {
    path: routes.tenantStaffStaff,
    title: 'Tenant Staff Admins',
  },
  {
    path: routes.tenantStaffConsolidatedReport,
    title: 'Consolidated Report',
  },
];

export const useMatchRoutes = () => {
  const matches = useMatches();
  const location = useLocation();

  const returnValue = useMemo(() => {
    return matches
      .slice(1)
      .map((c) => routesMeta.find((d) => d.path === c.pathname));
  }, [matches]);

  const isCurrentPath = useCallback(
    (path: string) => {
      if (path === routes.home) {
        return location.pathname === path;
      }

      return (
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
    },
    [location.pathname]
  );

  return { matches: returnValue, isCurrentPath };
};
